import { mediaUrl } from '@/lib/mediaUrl';
import { sanitizeHtml } from '@/utils/sanitize';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, BookOpen, Loader2 } from 'lucide-react';
import { loadYTApi } from './shared';

const looksLikeHtml = (t) => /<\/?[a-z][\s\S]*>/i.test(t || '');
const Transcript = ({ text, testId }) => (looksLikeHtml(text)
  ? <div className="mt-3 p-4 bg-slate-50 rounded-lg text-sm text-slate-700 max-h-64 overflow-y-auto border border-slate-200 prose prose-sm max-w-none" data-testid={testId} dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />
  : <div className="mt-3 p-4 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto border border-slate-200" data-testid={testId}>{text}</div>);

const VideoElement = ({ config, element, onComplete, isCompleted }) => {
  const url = mediaUrl(config.url || config.file_url || '');
  const [showTranscript, setShowTranscript] = useState(false);
  const [watchPct, setWatchPct] = useState(0);
  const [canComplete, setCanComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const videoRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const pollingRef = useRef(null);
  const watchedRef = useRef(new Set());
  const durationRef = useRef(0);
  const ytDivId = `yt-player-${element.id}`;
  const threshold = (config.completion_threshold || 0.9) * 100;

  const isYouTube = url && (url.includes('youtube.com') || url.includes('youtu.be'));
  const isVimeo = url && url.includes('vimeo.com');
  const isDirectVideo = url && !isYouTube && !isVimeo;

  const getYouTubeId = (u) => {
    try {
      if (u.includes('youtube.com/watch')) return new URL(u).searchParams.get('v');
      if (u.includes('youtu.be/')) return u.split('youtu.be/')[1].split(/[?#]/)[0];
    } catch (e) { console.error('Failed to parse YouTube URL:', e); }
    return '';
  };

  const getVimeoEmbedUrl = (u) => {
    const vid = u.split('vimeo.com/')[1];
    return vid ? `https://player.vimeo.com/video/${vid}` : u;
  };

  const updatePct = useCallback(() => {
    const dur = durationRef.current;
    if (dur > 0) {
      const pct = Math.min(100, Math.round((watchedRef.current.size / dur) * 100));
      setWatchPct(pct);
      if (pct >= threshold) setCanComplete(true);
    }
  }, [threshold]);

  useEffect(() => {
    if (!isYouTube || isCompleted || !url) return;
    let destroyed = false;
    const setup = async () => {
      await loadYTApi();
      if (destroyed) return;
      const videoId = getYouTubeId(url);
      if (!videoId) return;
      ytPlayerRef.current = new window.YT.Player(ytDivId, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, enablejsapi: 1 },
        events: {
          onReady: (evt) => {
            const dur = Math.floor(evt.target.getDuration()) || 0;
            durationRef.current = dur;
            if (dur === 0) { setTimeout(() => { if (!destroyed) setCanComplete(true); }, 30000); }
          },
          onStateChange: (evt) => {
            if (evt.data === window.YT.PlayerState.PLAYING) {
              if (!pollingRef.current) {
                pollingRef.current = setInterval(() => {
                  if (!ytPlayerRef.current?.getCurrentTime) return;
                  watchedRef.current.add(Math.floor(ytPlayerRef.current.getCurrentTime()));
                  updatePct();
                }, 1000);
              }
            } else { clearInterval(pollingRef.current); pollingRef.current = null; }
          },
        },
      });
    };
    setup();
    return () => { destroyed = true; clearInterval(pollingRef.current); pollingRef.current = null; try { ytPlayerRef.current?.destroy?.(); } catch (e) { console.error('YT player cleanup:', e); } ytPlayerRef.current = null; };
  }, [url, isYouTube, isCompleted, ytDivId, updatePct]);

  useEffect(() => {
    if (!isDirectVideo || isCompleted) return;
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => { durationRef.current = Math.floor(v.duration) || 0; };
    const onTime = () => { watchedRef.current.add(Math.floor(v.currentTime)); updatePct(); };
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('timeupdate', onTime);
    return () => { v.removeEventListener('loadedmetadata', onMeta); v.removeEventListener('timeupdate', onTime); };
  }, [isDirectVideo, isCompleted, updatePct]);

  useEffect(() => {
    if (isDirectVideo || isYouTube || !url || isCompleted) return;
    const estimatedDuration = (element.duration_minutes || 5) * 60;
    durationRef.current = estimatedDuration;
    let elapsed = 0;
    const timer = setInterval(() => { if (document.visibilityState === 'visible') { elapsed++; watchedRef.current.add(elapsed); updatePct(); } }, 1000);
    return () => clearInterval(timer);
  }, [url, isDirectVideo, isYouTube, isCompleted, element.duration_minutes, updatePct]);

  const handleMarkComplete = async () => {
    setCompleting(true);
    try { await onComplete(); } catch { toast.error('Failed to save video completion. Please try again.'); } finally { setCompleting(false); }
  };

  return (
    <div className="space-y-4">
      {isYouTube && !isCompleted && <div className="aspect-video rounded-xl overflow-hidden bg-slate-900"><div id={ytDivId} className="w-full h-full" /></div>}
      {isYouTube && isCompleted && <div className="aspect-video rounded-xl overflow-hidden bg-slate-900"><iframe src={`https://www.youtube.com/embed/${getYouTubeId(url)}`} className="w-full h-full" allowFullScreen title={config.title || 'Video'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" /></div>}
      {isVimeo && <div className="aspect-video rounded-xl overflow-hidden bg-slate-900"><iframe src={getVimeoEmbedUrl(url)} className="w-full h-full" allowFullScreen title={config.title || 'Video'} allow="autoplay; fullscreen; picture-in-picture" /></div>}
      {isDirectVideo && <video ref={videoRef} controls crossOrigin={config.captions_url ? "anonymous" : undefined} className="w-full rounded-xl bg-slate-900" data-testid="direct-video"><source src={url} />{config.captions_url && <track kind="captions" src={mediaUrl(config.captions_url)} srcLang="en" label="English" default data-testid="video-captions-track" />}</video>}
      {!url && <div className="aspect-video bg-slate-100 rounded-xl flex items-center justify-center"><p className="text-slate-400">No video configured</p></div>}
      {!isCompleted && url && (
        <div className="space-y-2" data-testid="video-progress-tracker">
          <div className="flex items-center justify-between text-xs text-slate-500"><span>{Math.round(watchPct)}% watched</span><span>{Math.round(threshold)}% required to complete</span></div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all duration-300 ${canComplete ? 'bg-green-500' : 'bg-[#20acf6]'}`} style={{ width: `${watchPct}%` }} /></div>
        </div>
      )}
      {config.transcript && (
        <div>
          <Button variant="outline" size="sm" onClick={() => setShowTranscript(!showTranscript)} className="text-xs gap-1.5" data-testid="video-transcript-btn"><BookOpen className="h-3.5 w-3.5" />{showTranscript ? 'Hide Transcript' : 'Transcript'}</Button>
          {showTranscript && <Transcript text={config.transcript} testId="video-transcript-content" />}
        </div>
      )}
      {!isCompleted && (
        <Button onClick={handleMarkComplete} disabled={!canComplete || completing} className={canComplete ? 'bg-[#20acf6] hover:bg-[#3bb8f7]' : 'bg-slate-300 cursor-not-allowed'} data-testid="mark-complete-btn">
          {completing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
          {completing ? 'Saving...' : canComplete ? 'Mark as Watched' : `Watch ${Math.round(threshold)}% to continue`}
        </Button>
      )}
    </div>
  );
};

export default VideoElement;
